// Actividad — what other people did that concerns the reader (ios/Sources/Views/ActivityPane.swift):
// Menciones (the search machinery pointed at the reader's mention keys, cache first, server behind
// it — shared/API.md §23.3; RecentMentionsSheet.swift) and Reacciones (who reacted to the reader's
// own messages, from the store).
import { placeIcon } from './PlaceLabel';
import { useEffect, useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import type { MentionsServerResult } from '../../../data/features/destinations';
import { t } from '../../../i18n/i18n';
import { DestinationFrame, EmptyState, InlineStatus, Segmented } from '../../kit/Kit';
import { Highlighted, PostResultRow } from '../../kit/PostResultRow';
import { useLive, useLocalStorage, useMinuteClock } from '../../hooks';
import { useShell } from '../../shell/ShellContext';
import { buildReactionRows, buildResultRows, mergeMentions } from './destinationsModel';
import { PlaceLabel } from './PlaceLabel';
import './destinations.css';

type ActivityFilter = 'mentions' | 'reactions';

export function ActivityView() {
  const shell = useShell();
  const [filter, setFilter] = useLocalStorage<ActivityFilter>('satori.activity.filter', 'mentions');
  return (
    <DestinationFrame
      icon="activity"
      title={t('channelList.tab.activity')}
      subtitle={shell.team.displayName}
      toolbar={
        <Segmented<ActivityFilter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'mentions', label: t('mentions.title') },
            { value: 'reactions', label: t('activity.reactions') },
          ]}
        />
      }
    >
      {filter === 'reactions' ? <ReactionsList /> : <MentionsList teamId={shell.team.id} />}
    </DestinationFrame>
  );
}

function MentionsList({ teamId }: { teamId: string }) {
  const shell = useShell();
  const now = useMinuteClock();
  const { value: local, loadedFor } = useLive(() => data.call('mentionsLocal', teamId), ['posts', 'users', 'channels', 'channelParticipants', 'syncState'], [teamId]);
  const [server, setServer] = useState<MentionsServerResult | null>(null);
  const [asking, setAsking] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setAsking(true);
    setServer(null);
    data.call('mentionsServer', teamId).then(
      (r) => {
        if (!alive) return;
        setServer(r);
        setAsking(false);
      },
      () => {
        if (!alive) return;
        setServer({ ok: false, keys: [], posts: [], users: [], places: {} });
        setAsking(false);
      },
    );
    return () => {
      alive = false;
    };
  }, [teamId, attempt]);

  const merged = useMemo(() => mergeMentions(local, server), [local, server]);
  const keys = local?.keys.length ? local.keys : server?.keys ?? [];
  const rows = useMemo(() => buildResultRows(merged.snapshot, now, { keys }), [merged, now, keys]);

  const notice =
    merged.notice === 'offline' ? (
      <InlineStatus kind="error" text={t('mentions.cachedOnly')} onRetry={() => setAttempt((n) => n + 1)} />
    ) : merged.notice === 'cachedOnly' ? (
      <InlineStatus kind="loading" text={t('search.cachedOnly')} />
    ) : null;

  if (!rows.length) {
    // An empty CACHE is a wait, not an answer: "no mentions" only once the server has spoken.
    if (asking || !loadedFor) return <InlineStatus kind="loading" text={t('web.destinations.mentions.searching')} />;
    return (
      <>
        {notice}
        <EmptyState title={t('mentions.empty')} />
      </>
    );
  }
  return (
    <>
      {asking ? <InlineStatus kind="loading" text={t('web.destinations.mentions.searching')} /> : notice}
      <div className="dest-results">
        {rows.map((row) => (
          <PostResultRow
            key={row.postId}
            userId={row.userId}
            authorName={row.authorName}
            authorVersion={row.authorVersion}
            place={row.place}
            placeIsPrivate={row.placeType === 'P'}
            placeIcon={placeIcon(row.placeType)}
            time={row.timeLabel}
            snippet={<Highlighted text={row.text} ranges={row.highlights} />}
            onOpen={() => shell.jumpToPost(row.postId)}
          />
        ))}
      </div>
    </>
  );
}

function ReactionsList() {
  const shell = useShell();
  const now = useMinuteClock();
  const { value } = useLive(() => data.call('reactionActivity'), ['postReactions', 'posts', 'users', 'channels', 'channelParticipants'], []);
  const rows = useMemo(() => (value ? buildReactionRows(value, now) : null), [value, now]);
  if (!rows) return null;
  if (!rows.length) return <EmptyState title={t('activity.reactions.empty')} />;
  return (
    <div className="dest-list" role="list">
      {rows.map((row) => (
        <div
          key={row.id}
          role="listitem"
          tabIndex={0}
          className="dest-row reaction-row"
          onClick={() => shell.jumpToPost(row.postId)}
          onKeyDown={(e) => (e.key === 'Enter' ? shell.jumpToPost(row.postId) : undefined)}
        >
          <span className="reaction-emoji" aria-hidden="true">
            {row.emoji}
          </span>
          <div className="dest-row-main">
            <div className="dest-row-head">
              <strong className="dest-actor">{row.actorName}</strong>
              <PlaceLabel name={row.channelName} type={value?.places[row.channelId]?.type ?? ''} />
              <span className="dest-time">{row.timeLabel}</span>
            </div>
            <div className="dest-preview">{row.preview}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
